import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Autocomplete, Fade } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEndpointList } from "@/store/ses/sesEndpointSlice";
import { selectActiveRobotVersion, selectRobotList } from "@/store/ses/sesRobotSlice";
import {
	changeBooleanValuesFreeswitchConnector,
	changeConnectorName,
	changeStringValuesFreeswitchConnector,
	changeTypeFreeswitchConnector,
} from "@/store/ses/sesConnectorSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import { FREESWITCH_CONNECTOR_TYPES_LIST } from "@/constants/robotConfigLists";
import { ENDPOINT } from "@/constants/defaultValues";
import { IEndpoint } from "@/types/ses/sesItemConfigTypes";
import { IDataRobotList } from "@/types/ses/sesRobotTypes";
import { TSubtypeFreeswitsh } from "@/types/ses/sesConnectorTypes";
import { RenderOption, CustomTextField, CustomMenuItem, CustomCheckbox, NoOptionText, H3 } from "@/ui";
import { IFreeswitchProps } from "./Freeswitch.props";
import styles from "./Freeswitch.module.scss";

const Freeswitch = ({ selectId, channel, connectorName, settings, changeFlgHandler }: IFreeswitchProps): JSX.Element => {
	const [inputConnectorName, setInputConnectorName] = useState<string>(""); // название коннектора
	const [selectServiceMode, setSelectServiceMode] = useState<TSubtypeFreeswitsh>("robot"); // режим обслуживания
	const [inputDestination, setInputDestination] = useState<string>(""); // внутренний номер
	const [inputAssistant, setInputAssistant] = useState<IDataRobotList | null>(null); // ассистент (робот)
	const [inputCallTransfer, setInputCallTransfer] = useState<string>(""); // перевод звонка
	const [inputEndpoint, setInputEndpoint] = useState<IEndpoint | null>(null); // КТ
	const [checkAutostartAssistant, setCheckAutostartAssistant] = useState<boolean>(false); // автостарт ассистента

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const robotList = useAppSelector(selectRobotList); // store - список роботов
	const endpointList = useAppSelector(selectEndpointList); // список КТ

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const isDisabled = useMemo(() => {
		return !isAccess(SES.CONNECTOR_MODIFY) || activeRobotVersion === "backup";
	}, [activeRobotVersion]);

	// следим за id - вписываем значения
	useEffect(() => {
		setInputConnectorName(connectorName);
		setSelectServiceMode(settings.serviceMode);
		setInputDestination(settings.destination);
		const getRobotItem = (): IDataRobotList | null => {
			if ("assistant" in settings && settings.assistant) {
				const foundRobot = robotList.data.find((robot) => robot.id === settings.assistant || robot.id + "_prod" === settings.assistant);
				const notFoundRobot = {
					id: settings.assistant,
					hasBackup: false,
					hasProd: false,
					name: `${settings.assistant} (${translate("title_notFound")})`,
					destinations: [],
				};
				// если робот есть в списке - добавляем в имя версию
				if (foundRobot) return { ...foundRobot, name: `${foundRobot.name} (${translate(settings.assistant.includes("_prod") ? "title_working" : "title_draft")})` };
				else return notFoundRobot;
			} else return null;
		};
		setInputAssistant(getRobotItem());
		"cc_destination" in settings && setInputCallTransfer(settings.cc_destination);
		const foundEndpoint =
			"endpoint" in settings && settings.endpoint
				? endpointList.data.find((endpointItem) => endpointItem.id === settings.endpoint) || {
						...ENDPOINT,
						name: `${settings.endpoint} (${translate("title_notFound")})`,
				  }
				: null;
		setInputEndpoint(foundEndpoint);
		setCheckAutostartAssistant(settings.autostart);
	}, [selectId, connectorName, channel, settings]);

	// обработчик изменения названия коннектора
	const connectorNameChangeHandler = (): void => {
		if (connectorName === inputConnectorName) return;
		dispatch(changeConnectorName({ newName: inputConnectorName }));
		changeFlgHandler("name");
	};

	// обработчик изменения типа
	const typeChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as TSubtypeFreeswitsh;
		setSelectServiceMode(value);
		dispatch(changeTypeFreeswitchConnector({ serviceMode: value }));
		changeFlgHandler("params");
	};

	// обработчик изменения внутреннего номера
	const destinationChangeHandler = (): void => {
		if (settings.destination === inputDestination) return;
		dispatch(changeStringValuesFreeswitchConnector({ channel, field: "destination", value: inputDestination }));
		changeFlgHandler("params");
	};

	// обработчик изменения ассистента
	const assistantChangeHandler = (): void => {
		if ("assistant" in settings && settings.assistant === inputAssistant?.id) return;
		dispatch(changeStringValuesFreeswitchConnector({ channel, field: "assistant", value: inputAssistant?.id || "" }));
		changeFlgHandler("params");
	};

	// обработчик изменения номера для перевода звонка
	const сallTransferChangeHandler = (): void => {
		if ("cc_destination" in settings && settings.cc_destination === inputCallTransfer) return;
		dispatch(changeStringValuesFreeswitchConnector({ channel, field: "cc_destination", value: inputCallTransfer }));
		changeFlgHandler("params");
	};

	// обработчик изменения КТ
	const endpointChangeHandler = (): void => {
		if ("endpoint" in settings && settings.endpoint === inputEndpoint?.id) return;
		dispatch(changeStringValuesFreeswitchConnector({ channel, field: "endpoint", value: inputEndpoint?.id || "" }));
		changeFlgHandler("params");
	};

	// обработчик изменения флага автостарта ассистента
	const autostartAssistantChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		setCheckAutostartAssistant(e.target.checked);
		dispatch(changeBooleanValuesFreeswitchConnector({ channel, field: "autostart", value: e.target.checked }));
		changeFlgHandler("params");
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				{/* общие настройки */}
				<H3 className={styles.header}>{translate("title_generalSettings")}</H3>
				<div className={styles.containerInner}>
					{/* название коннектора */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_connectorName")}
						value={inputConnectorName}
						onChange={(e) => setInputConnectorName(e.target.value)}
						onBlur={connectorNameChangeHandler}
						error={!inputConnectorName}
						disabled={isDisabled}
						hint={"hint_connectorsTitle"}
						hintPosition="left"
					/>
					{/* режим обслуживания */}
					<CustomTextField
						required
						select
						fullWidth
						label={translate("select_serviceMode")}
						value={selectServiceMode}
						onChange={typeChangeHandler}
						error={!selectServiceMode}
						disabled={isDisabled}
						hint={"hint_connectorsServiceMode"}
						hintPosition="top"
					>
						{FREESWITCH_CONNECTOR_TYPES_LIST.map(({ type, translation }) => (
							<CustomMenuItem key={type} value={type} hint={`hint_connectors${type.charAt(0).toUpperCase() + type.slice(1)}Mode`} hintPosition="left">
								{translate(translation)}
							</CustomMenuItem>
						))}
					</CustomTextField>
				</div>

				<div className={styles.containerInner}>
					{/* внутренний номер */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_internalNumber")}
						value={inputDestination}
						onChange={(e) => setInputDestination(e.target.value)}
						onBlur={destinationChangeHandler}
						error={!inputDestination}
						disabled={isDisabled}
						hint={"hint_connectorsFreeswitchInternalNumber"}
						hintPosition="left"
					/>
					{/* ассистент (робот) */}
					{selectServiceMode === "robot" && (
						<Autocomplete
							fullWidth
							autoHighlight
							options={[
								...robotList.data
									.filter((robot) => robot.id !== robotList.activeRobotId) // кроме самого себя
									.map((robot) => {
										const arrayRobotVersions = [{ ...robot, name: `${robot.name} (${translate("title_draft")})` }];
										if (robot.hasProd) arrayRobotVersions.push({ ...robot, id: robot.id + "_prod", name: `${robot.name} (${translate("title_working")})` });
										return arrayRobotVersions;
									}) // раздвоение на черновик/прод с подписью версий
									.flat(),
							]}
							value={inputAssistant}
							onChange={(_, value) => setInputAssistant(value)}
							onBlur={assistantChangeHandler}
							disabled={isDisabled}
							noOptionsText={<NoOptionText />}
							renderInput={(params) => <CustomTextField {...params} label={translate("input_assistant")} />}
							getOptionLabel={(option) => option.name}
							renderOption={(props, option) => {
								return (
									<RenderOption {...props} key={option.id}>
										{option.name}
									</RenderOption>
								);
							}}
						/>
					)}
					{/* номер для перевода звонка */}
					{selectServiceMode === "assistant" && (
						<CustomTextField
							fullWidth
							required
							label={translate("input_callTransferNumber")}
							value={inputCallTransfer}
							onChange={(e) => setInputCallTransfer(e.target.value)}
							onBlur={сallTransferChangeHandler}
							error={!inputCallTransfer}
							disabled={isDisabled}
						/>
					)}
				</div>

				{selectServiceMode === "robot" && (
					<div className={styles.containerInner}>
						{/* КТ */}
						<Autocomplete
							autoHighlight
							options={endpointList.data}
							value={inputEndpoint}
							onChange={(_, value) => setInputEndpoint(value)}
							onBlur={endpointChangeHandler}
							noOptionsText={<NoOptionText />}
							disabled={isDisabled}
							renderInput={(params) => <CustomTextField {...params} label={translate("select_endpoint")} />}
							getOptionLabel={(option) => option.name}
							renderOption={(props, option) => {
								return (
									<RenderOption {...props} key={option.id}>
										{option.name}
									</RenderOption>
								);
							}}
							sx={{ width: "calc(50% - 6px)" }}
						/>
					</div>
				)}

				<div className={styles.containerInner}>
					<CustomCheckbox label="checkbox_autostartAssistant" checked={checkAutostartAssistant} onChange={autostartAssistantChangeHandler} disabled={isDisabled} />
				</div>
			</div>
		</Fade>
	);
};

export default Freeswitch;
